import JSZip from "jszip";

const trackRevisionsTagRegex = /<w:trackRevisions[^>]*>/i;
const trackRevisionsDisabledRegex = /\bw:val="(?:false|0)"/i;
const revisionNodeRegex = /<w:(?:ins|del|moveFrom|moveTo)\b/i;
const commentRangeRegex =
  /<w:(?:commentRangeStart|commentRangeEnd|commentReference)\b/i;
const commentDefinitionRegex = /<w:comment\b/i;

export type TrackChangeStatus = {
  hasTrackRevisionsSetting: boolean;
  hasRevisionNodes: boolean;
  hasCommentNodes: boolean;
};

const defaultStatus: TrackChangeStatus = {
  hasTrackRevisionsSetting: false,
  hasRevisionNodes: false,
  hasCommentNodes: false,
};

const readXml = async (file?: JSZip.JSZipObject | null) =>
  file ? await file.async("text") : null;

const collectXmlFiles = (
  zip: JSZip,
  pattern: RegExp,
): Array<{ file: JSZip.JSZipObject }> =>
  zip.file(pattern).map((file) => ({
    file,
  }));

export async function analyzeTrackChanges(
  fileBuffer: ArrayBuffer | Blob,
): Promise<TrackChangeStatus> {
  try {
    const buffer =
      fileBuffer instanceof Blob ? await fileBuffer.arrayBuffer() : fileBuffer;
    const zip = await JSZip.loadAsync(buffer);

    const settingsXml = await readXml(zip.file("word/settings.xml"));

    let hasTrackRevisionsSetting = false;
    if (settingsXml) {
      const trackRevisionsMatch = trackRevisionsTagRegex.exec(settingsXml);
      if (trackRevisionsMatch) {
        const tag = trackRevisionsMatch[0];
        const isDisabled = trackRevisionsDisabledRegex.test(tag);
        hasTrackRevisionsSetting = !isDisabled;
      }
    }

    const parts = [
      {
        file: zip.file("word/document.xml"),
      },
      ...collectXmlFiles(zip, /^word\/header.*\.xml$/),
      ...collectXmlFiles(zip, /^word\/footer.*\.xml$/),
      ...collectXmlFiles(zip, /^word\/footnotes.*\.xml$/),
      ...collectXmlFiles(zip, /^word\/endnotes.*\.xml$/),
    ].filter((entry): entry is { file: JSZip.JSZipObject } =>
      Boolean(entry.file),
    );

    const status: TrackChangeStatus = {
      ...defaultStatus,
      hasTrackRevisionsSetting,
    };

    await Promise.all(
      parts.map(async ({ file }) => {
        const xml = await readXml(file);
        if (!xml) {
          return;
        }

        if (revisionNodeRegex.test(xml)) {
          status.hasRevisionNodes = true;
        }

        if (commentRangeRegex.test(xml)) {
          status.hasCommentNodes = true;
        }
      }),
    );

    const commentsXml = await readXml(zip.file("word/comments.xml"));
    if (commentsXml && commentDefinitionRegex.test(commentsXml)) {
      status.hasCommentNodes = true;
    }

    return status;
  } catch {
    return defaultStatus;
  }
}
